import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import StatCard from '../components/StatCard';
import TradeTable from '../components/TradeTable';
import BalanceChart from '../components/BalanceChart';
import Withdrawals from '../components/Withdrawals';
import AmkLogo from '../components/AmkLogo';
import { useAuth } from '../context/AuthContext';
import LivePrices from '../components/LivePrices';
import PnlCalendar from '../components/PnlCalendar';
import Settings from '../components/Settings';
import NotificationBell from '../components/NotificationBell';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useEventStream } from '../hooks/useEventStream';
import { UserTrade, PortfolioStats, BalanceHistory } from '../types';
import { api } from '../api';
import './Dashboard.css';

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);

const VALID_TABS = ['dashboard', 'trades', 'withdrawals', 'history', 'settings'];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeTab = tab && VALID_TABS.includes(tab) ? tab : 'dashboard';
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleTabChange = useCallback((tabId: string) => {
    navigate(tabId === 'dashboard' ? '/' : `/${tabId}`);
  }, [navigate]);

  const [trades, setTrades] = useState<UserTrade[]>([]);
  const [stats, setStats] = useState<PortfolioStats | null>(null);
  const [balanceHistory, setBalanceHistory] = useState<BalanceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [liveRefreshTrigger, setLiveRefreshTrigger] = useState(0);

  const refreshData = useCallback(async () => {
    try {
      const [tradesRes, statsData, historyData] = await Promise.all([
        api.getTrades(),
        api.getPortfolioStats(),
        api.getBalanceHistory('ALL'),
      ]);
      setTrades(tradesRes.items || tradesRes);
      setStats(statsData);
      setBalanceHistory(historyData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    }
  }, []);

  useEffect(() => {
    refreshData().finally(() => setLoading(false));
  }, [refreshData]);

  useEventStream({
    onTradeOpen: () => { refreshData(); setLiveRefreshTrigger(t => t + 1); },
    onTradeClose: () => { refreshData(); setLiveRefreshTrigger(t => t + 1); },
    onBalanceUpdate: () => refreshData(),
  });

  const openTrades = trades.filter(tr => tr.status === 'OPEN');
  const closedTrades = trades.filter(tr => tr.status === 'CLOSED');

  const headerTitles: Record<string, { title: string; subtitle: string }> = {
    dashboard: { title: t('header.welcomeBack', { name: user?.firstName }), subtitle: t('header.portfolioGlance') },
    trades: { title: t('header.tradesTitle'), subtitle: t('header.tradesSubtitle') },
    withdrawals: { title: t('header.withdrawalsTitle'), subtitle: t('header.withdrawalsSubtitle') },
    history: { title: t('header.historyTitle'), subtitle: t('header.historySubtitle') },
    settings: { title: t('header.settingsTitle'), subtitle: t('header.settingsSubtitle') },
  };

  const { title: pageTitle, subtitle: pageSubtitle } = headerTitles[activeTab] || headerTitles.dashboard;

  const mobileTopBar = (
    <div className="fixed top-0 left-0 right-0 h-14 bg-[#0b0a0f]/95 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 z-50 md:hidden">
      <button onClick={() => setSidebarOpen(true)} className="p-1.5 text-zinc-400 hover:text-white">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
      </button>
      <AmkLogo size={26} />
      <div className="flex items-center gap-2">
        <LanguageSwitcher />
        <NotificationBell />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#07070b]">
        {mobileTopBar}
        <Sidebar activeTab={activeTab} onTabChange={handleTabChange} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-[260px] p-4 pt-[72px] md:pt-8 md:p-8 flex items-center justify-center">
          <span className="text-zinc-400">{t('common.loading')}</span>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#07070b]">
      {mobileTopBar}
      <Sidebar activeTab={activeTab} onTabChange={handleTabChange} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="flex-1 md:ml-[260px] p-4 pt-[72px] md:pt-8 md:p-8 lg:p-10 flex flex-col gap-4 md:gap-6 overflow-x-hidden">
        <header className="flex justify-between items-start">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight">{pageTitle}</h1>
            <p className="text-sm text-white/35 mt-1">{pageSubtitle}</p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <NotificationBell />
          </div>
        </header>

        {activeTab === 'dashboard' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
              <StatCard label={t('stats.totalBalance')} value={formatCurrency(stats?.totalBalance || user?.balance || 0)} change={t('stats.allTime', { value: stats?.totalProfitPercentage?.toFixed(1) || '0' })} changeType={(stats?.totalProfit || 0) >= 0 ? 'positive' : 'negative'} accent="#d4a237" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>} />
              <StatCard label={t('stats.totalProfit')} value={formatCurrency(stats?.totalProfit || 0)} change={t('stats.allTime', { value: stats?.totalProfitPercentage?.toFixed(1) || '0' })} changeType={(stats?.totalProfit || 0) >= 0 ? 'positive' : 'negative'} accent="#34d399" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-5" /></svg>} />
              <StatCard label={t('stats.thisWeek')} value={formatCurrency(stats?.weeklyPnl || 0)} change={t('stats.tradesAndReferrals')} changeType={(stats?.weeklyPnl || 0) >= 0 ? 'positive' : 'negative'} accent="#f6d77a" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>} />
              <StatCard label={t('stats.today')} value={formatCurrency(stats?.todayPnl || 0)} changeType={(stats?.todayPnl || 0) >= 0 ? 'positive' : 'negative'} accent="#e8c062" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>} />
            </div>

            {stats?.referralStats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
                <StatCard label={t('stats.referralTotal')} value={formatCurrency(stats.referralStats.totalEarnings)} change={t('stats.referralCount', { count: stats.referralStats.referralCount })} changeType="positive" accent="#f6d77a" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>} />
                <StatCard label={t('stats.referralWeekly')} value={formatCurrency(stats.referralStats.weeklyEarnings)} changeType={(stats.referralStats.weeklyEarnings) > 0 ? 'positive' : 'neutral'} accent="#e8c062" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>} />
                <StatCard label={t('stats.tradeProfitOnly')} value={formatCurrency(stats?.tradeProfitOnly || 0)} change={t('stats.winRate', { value: stats?.winRate?.toFixed(0) || '0' })} changeType={(stats?.tradeProfitOnly || 0) >= 0 ? 'positive' : 'negative'} accent="#34d399" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>} />
                <StatCard label={t('stats.managementFee')} value={`${user?.feePercentage || 0}%`} change={t('stats.onProfitableTrades')} changeType="neutral" accent="#c9952e" icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>} />
              </div>
            )}

            <BalanceChart data={balanceHistory} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PnlCalendar />
              <div className="dashboard-summary-card">
                <h3>{t('summary.tradingOverview')}</h3>
                <div className="summary-rows">
                  <div className="summary-row"><span>{t('summary.todayPnl')}</span><span className={(stats?.todayPnl || 0) >= 0 ? 'positive' : 'negative'}>{formatCurrency(stats?.todayPnl || 0)}</span></div>
                  <div className="summary-row"><span>{t('summary.weeklyPnl')}</span><span className={(stats?.weeklyPnl || 0) >= 0 ? 'positive' : 'negative'}>{formatCurrency(stats?.weeklyPnl || 0)}</span></div>
                  <div className="summary-row"><span>{t('summary.monthlyPnl')}</span><span className={(stats?.monthlyPnl || 0) >= 0 ? 'positive' : 'negative'}>{formatCurrency(stats?.monthlyPnl || 0)}</span></div>
                  <div className="summary-row"><span>{t('summary.bestTrade')}</span><span className={(stats?.bestTrade || 0) >= 0 ? 'positive' : 'negative'}>{stats?.bestTrade ? (stats.bestTrade > 0 ? '+' : '') + formatCurrency(stats.bestTrade) : '—'}</span></div>
                  <div className="summary-row"><span>{t('summary.worstTrade')}</span><span className={(stats?.worstTrade || 0) >= 0 ? 'positive' : 'negative'}>{stats?.worstTrade ? formatCurrency(stats.worstTrade) : '—'}</span></div>
                  <div className="summary-row"><span>{t('summary.closedTrades')}</span><span>{stats?.closedTrades || 0}</span></div>
                  <div className="summary-row"><span>{t('summary.winRate')}</span><span className="positive">{stats?.winRate?.toFixed(1) || '0'}%</span></div>
                  <div className="summary-row"><span>{t('summary.openPositions')}</span><span className="highlight">{stats?.openTrades || 0}</span></div>
                </div>
              </div>
            </div>

            <LivePrices refreshTrigger={liveRefreshTrigger} />
            <TradeTable trades={openTrades} title={t('trades.openPositions')} showProfit={false} />
            <TradeTable trades={closedTrades} title={t('trades.tradeHistory')} />
          </>
        )}

        {activeTab === 'trades' && (
          <>
            <LivePrices refreshTrigger={liveRefreshTrigger} />
            <TradeTable trades={openTrades} title={t('trades.openPositions')} showProfit={false} />
            <TradeTable trades={closedTrades} title={t('trades.closedTrades')} />
          </>
        )}

        {activeTab === 'withdrawals' && <Withdrawals />}
        {activeTab === 'history' && <TradeTable trades={closedTrades} title={t('trades.completeHistory')} />}
        {activeTab === 'settings' && <Settings />}
      </main>
    </div>
  );
};

export default Dashboard;
